import { Component, Inject } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { AuthService } from "app/auth/auth.service";
import { ISB303CompliancePdfDto } from "app/models/303/sb303CompliancePdf";
import { INote } from "app/models/notes/note";
import { SB303Service } from "app/services/303/303.service";
import { SharedService } from "app/services/core/shared.service";
import { NoteService } from "app/services/notes/notes.service";
import { ToastrService } from "ngx-toastr";

export const DateFormats = {
  parse: {
    dateInput: ['MM/DD/YYYY']
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
    selector: 'app-dialog-print-compliance-pdf',
    templateUrl: './print-compliance-pdf.component.html',
    providers: [{ provide: MAT_DATE_FORMATS, useValue: DateFormats }],
    standalone: false
})

export class DialogPrintCompliancePDFComponent {

  public printPdfForm = new FormGroup({
    licenseId: new FormControl('', Validators.required),
    from: new FormControl('', Validators.required),
    to: new FormControl('', Validators.required)
  });

  public isCreatingReport = false;

  constructor(public dialogRef: MatDialogRef<DialogPrintCompliancePDFComponent>,
    public sb303Service: SB303Service,
    public sharedService: SharedService,
    public toastr: ToastrService,
    public authService: AuthService,
    public noteService: NoteService,
    private dialog: MatDialog) { }


  createReport(): void {
    if (this.isCreatingReport) return;

    this.isCreatingReport = true;
    let form = this.printPdfForm.value;

    this.sb303Service.getCompliancePdfData(form.licenseId, new Date(form.from).toDateString(), new Date(form.to).toDateString()).subscribe({
      next: response => {
        this.downloadPDF(response);
      },
      complete: () => {
        this.isCreatingReport = false;
      },
      error: e => {
        this.isCreatingReport = false;
        console.log('error', e);
      }
    });

  }

  downloadPDF(sb303PDF: ISB303CompliancePdfDto): void {
    let clientDataNotes: INote[] = [];
    let serviceCenterDataNotes: INote[] = [];

    this.noteService.getNotesByEntity(sb303PDF.sB303.clientData.id.toString(), "ClientData").subscribe({
      next: response => clientDataNotes = response,
      complete: () => {
        sb303PDF.clientDataNotes = clientDataNotes;
      },
      error: e => {
        console.log('error', e);
      }
    });

    this.noteService.getNotesByEntity(sb303PDF.sB303.serviceCenterData.id.toString(), "ServiceCenterData").subscribe({
      next: response => serviceCenterDataNotes = response,
      complete: () => {
        sb303PDF.serviceCenterDataNotes = serviceCenterDataNotes;
      },
      error: e => {
        console.log('error', e);
      }
    });

    const dialogRef = this.dialog.open(CompliancePdfDialog, {
      data: sb303PDF,
      width: '800px',
      height: 'auto',
      autoFocus: false,
      panelClass: 'hidden-dialog'
    });

    dialogRef.afterOpened().subscribe(async () => {
      await new Promise(resolve => setTimeout(resolve, 300));

      let snapshotPdf = document.querySelector('#snapshotPdf') as HTMLElement;

      if (!snapshotPdf) {
        const dialogs = document.querySelectorAll('mat-dialog-container');
        for (let i = 0; i < dialogs.length; i++) {
          snapshotPdf = dialogs[i].querySelector('#snapshotPdf') as HTMLElement;
          if (snapshotPdf) break;
        }
      }

      if (!snapshotPdf) {
        console.error('Could not find #snapshotPdf element');
        dialogRef.close();
        return;
      }

      snapshotPdf.style.paddingBottom = '16px';
      const drawOptions = {
        scale: 0.75,
        paperSize: 'A4',
        forcePageBreak: '.page-break',
        margin: { top: '1cm', left: '1cm', right: '1cm', bottom: '2.5cm' },
        template: (data) => `
          <div style="position: absolute; bottom: 0; left: 0; right: 0; text-align: center; font-size: 12px;">
              Page ${data.pageNum} of ${data.totalPages} | License ID: ${sb303PDF.sB303.licenseId} | Service Center name: ${sb303PDF.entityName} <br> Reporting Window Open Date: ${sb303PDF.sB303.reportingStartDate} | Reporting Window Close Date: ${sb303PDF.sB303.reportingEndDate}
          </div>
        `
      };

      const group = await drawDOM(snapshotPdf, drawOptions);

      const pdfDataUri: string = await exportPDF(group, drawOptions);

      const pdfBlob = this.dataUriToBlob(pdfDataUri);

      const filename = `SB303 Compliance PDF-${sb303PDF.sB303.licenseId}: ${sb303PDF.sB303.reportingStartDate}-${sb303PDF.sB303.reportingEndDate}.pdf`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      dialogRef.close();
    });
  }

  private dataUriToBlob(dataUri: string): Blob {
    const byteString = atob(dataUri.split(',')[1]);
    const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  cancel(): void {
    this.dialogRef.close('cancel');
  }
}

@Component({
    selector: "compliance-pdf-dialog",
    templateUrl: "./compliance-pdf.component.html",
    standalone: false
})

export class CompliancePdfDialog  {

  public currentDate = new Date();

  constructor(public dialogRef: MatDialogRef<CompliancePdfDialog>,
    public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data: ISB303CompliancePdfDto) {
        }

    ngOnInit() {
    }
}
